import { useEffect, useRef, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import useWebSocket, { ReadyState } from 'react-use-websocket';
import throttle from 'lodash/throttle';
import {
  mergeVehicleTracking,
  setStatisticVehicleStatus
} from 'src/features/vehicleSlice';
import { useAccessToken } from './useAccessToken';

const useVehicleTracking = () => {
  const dispatch = useDispatch();

  // Authentication & IDs
  const accessToken = useAccessToken();

  const listVehicleTracking = useSelector(s => s.vehicleSlice.listVehicleTracking);
  const { userId, idSocket, driverId } = useSelector(s => s.authSlice);

  // Refs: data queue and vehicle map
  const queueRef = useRef([]);
  const mapRef = useRef({});
  const intervalTimerRef = useRef(null);

  // WebSocket URL
  const wsUrl = useMemo(() => {
    if (!idSocket || !accessToken) return null;
    if (userId) {
      return `${process.env.REACT_APP_REALTIME_WSS_SERVER}/realtime/${idSocket}/enduser?token=${accessToken}`;
    }
    if (driverId) {
      return `${process.env.REACT_APP_REALTIME_WSS_SERVER}/realtime/${idSocket}/driver?token=${accessToken}`;
    }
    return `${process.env.REACT_APP_REALTIME_WSS_SERVER}/realtime/${idSocket}/agency?token=${accessToken}`;
  }, [idSocket, accessToken]);

  // Process queued updates (throttled)
  const processQueue = useCallback(() => {
    const updates = queueRef.current.splice(0);
    updates.forEach(u => {
      const current = mapRef.current[u.device_id];
      if (!current) return;
      const merged = { ...current, ...u };

      // compare relevant fields
      const changed = [
        'lat',
        'lng',
        'created_at',
        'speed_gps',
        'device_status',
        'direction',
        'engine_status'
      ].some(key => current[key] !== merged[key]);

      if (changed) {
        mapRef.current[u.device_id] = merged;
      }
    });

    dispatch(mergeVehicleTracking(Object.values(mapRef.current)));
  }, [dispatch]);

  const throttledProcess = useMemo(
    () => throttle(processQueue, 500, { leading: true, trailing: true }),
    [processQueue]
  );

  // WebSocket setup
  const { sendJsonMessage, readyState } = useWebSocket(wsUrl, {
    share: false,
    shouldReconnect: () => true,
    onOpen: () => {
      sendJsonMessage({
        event: 'subscribe',
        data: { channel: 'general-chatroom' }
      });
    },
    onMessage: useCallback(
      event => {
        if (!(event.data instanceof Blob)) return;
        const reader = new FileReader();
        reader.onload = () => {
          try {
            const v = JSON.parse(reader.result);
            const { deviceId, serial, data, timeStamp: ts, status } = v;
            if (!deviceId || !serial) return;

            const arr = data['254'];
            const [lat, lng] = Array.isArray(arr)
              ? arr
              : [undefined, undefined];

            queueRef.current.push({
              device_id: deviceId,
              device_serial: serial,
              created_at: ts,
              engine_status: data['259'],
              direction: data['255'],
              speed_gps: data['261'],
              device_status: status,
              ...(lat && lng && { lat, lng })
            });

            throttledProcess();
          } catch (e) {
            console.error('WS parse error', e);
          }
        };
        reader.readAsText(event.data);
      },
      [throttledProcess]
    )
  });

  // Initialize map from Redux list
  useEffect(() => {
    if (!listVehicleTracking || listVehicleTracking.length === 0) {
      mapRef.current = {};
      return;
    }

    const map = {};
    listVehicleTracking
      .filter(v => v.license_plate)
      .sort((a, b) => a.license_plate.localeCompare(b.license_plate))
      .forEach(v => {
        map[v.device_id] = { ...v };
      });
    mapRef.current = map;
  }, [listVehicleTracking]);

  // Calculate stats & check lost_gms every 5s
  useEffect(() => {
    const handler = () => {
      // Don't process if no vehicles
      if (Object.keys(mapRef.current).length === 0) {
        // Reset stats to 0 when no vehicles
        dispatch(setStatisticVehicleStatus({
          moving: 0,
          stopped: 0,
          lost_gps: 0,
          lost_gms: 0,
          over_speed: 0
        }));
        return;
      }

      const now = Date.now();
      const map = { ...mapRef.current }; // Clone to avoid direct mutation
      let hasMapChange = false;

      // Initialize stats from actual vehicle list
      const stats = {
        moving: 0,
        stopped: 0,
        lost_gps: 0,
        lost_gms: 0,
        over_speed: 0
      };

      // First pass: Count stats and check for lost_gms
      Object.values(map).forEach(v => {
        // Count current status
        if (v.device_status in stats) {
          stats[v.device_status]++;
        }

        // Check lost_gms logic
        const last = v.created_at * 1000;
        const diff = (now - last) / 1000;
        const shouldLost =
          (v.engine_status === 1 && diff > 300) ||  // 5 minutes
          (v.engine_status === 0 && diff > 3600);   // 60 minutes

        if (shouldLost && v.device_status !== 'lost_gms') {
          const oldStatus = v.device_status;
          const device_id = v.device_id;

          // Update vehicle status
          map[device_id] = { ...v, device_status: 'lost_gms' };
          hasMapChange = true;

          // Adjust stats: -1 from old status, +1 to lost_gms
          if (oldStatus && stats[oldStatus] !== undefined) {
            stats[oldStatus] = Math.max(0, stats[oldStatus] - 1);
          }
          stats.lost_gms++;
        }
      });

      // Dispatch updated stats
      dispatch(setStatisticVehicleStatus(stats));

      // Update map if there were lost_gms changes
      if (hasMapChange) {
        mapRef.current = map;
        dispatch(mergeVehicleTracking(Object.values(map)));
      }
    };

    // Run immediately on mount
    handler();

    // Then run every 5 seconds
    intervalTimerRef.current = setInterval(handler, 5000);

    // Cleanup
    return () => {
      if (intervalTimerRef.current) {
        clearInterval(intervalTimerRef.current);
      }
    };
  }, [dispatch]);

  return { readyState, sendJsonMessage };
};

export default useVehicleTracking;
